import { FC, Fragment } from "react"
import { useMutation, useQueryClient } from "react-query"

import RemoveBranchIcon from "@mui/icons-material/RemoveCircleOutline"

import { Button, Checkbox, Typography } from "@synapse-analytics/synapse-ui"
import { NotificationUtils } from "@synapse-analytics/synapse-ui"

import { VisionAPI } from "../../../../../API/VisionAPI"
import { definitions } from "../../../../../types/Generated/apiTypes"

import styles from "../CollectionsTab.module.scss"

type Branch = definitions["Branch"]
type Collection = definitions["Collection"]

/**
 * Props for the BranchItem component.
 */
interface BranchItemProps {
  /** The branch object to display */
  branch: Branch
  /** The collection that contains this branch */
  collection: Collection
  /** Whether the component is in edit mode (shows delete button instead of checkbox) */
  isEditMode: boolean
  /** Set of selected branch IDs for determining if this branch is selected */
  selectedBranchesSet: Set<number>
  /** Callback invoked when the branch checkbox is toggled */
  onToggle: (branchId: number, isChecked: boolean) => void
}

/**
 * BranchItem component displays and manages an individual branch within a collection.
 *
 * Features:
 * - Displays branch name with checkbox (normal mode) or delete button (edit mode)
 * - Handles branch deletion from collection
 * - Computes selection status internally from the selectedBranchesSet
 *
 * @param props - The component props
 * @returns The rendered branch item component
 */
const BranchItem: FC<BranchItemProps> = ({ branch, collection, isEditMode, selectedBranchesSet, onToggle }) => {
  const queryClient = useQueryClient()
  /** Determines if this branch is currently selected */
  const isSelected = selectedBranchesSet.has(branch.id!)

  /**
   * Mutation hook for updating collection branches.
   * Used when deleting a branch from a collection.
   * Invalidates collections and uncollected branches queries on success.
   */
  const { mutate: updateCollectionBranches, isLoading: isUpdatingBranches } = useMutation(
    ({ collectionId, branches_ids, name }: { collectionId: number; branches_ids: number[]; name: string }) => {
      return VisionAPI.updateCollection(collectionId, { name, branches_ids })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("fetchCollections")
        queryClient.invalidateQueries("fetchUncollectedBranches")
        NotificationUtils.toast("Collection branches updated successfully", {
          severity: "success",
        })
      },
      onError: (error: any) => {
        NotificationUtils.toast(`Failed to update collection branches: ${error?.message || "Unknown error"}`, {
          severity: "error",
        })
      },
    }
  )

  /**
   * Handles deletion of the branch from the collection.
   * Filters out the current branch and updates the collection with remaining branches.
   * Only proceeds if the collection has an ID.
   */
  const handleDelete = () => {
    if (!collection.id) return
    const updatedBranches = collection.branches?.filter((b) => b.id !== branch.id) ?? []
    updateCollectionBranches({
      collectionId: collection.id,
      branches_ids: updatedBranches.map((b) => b.id!),
      name: collection.name,
    })
  }

  if (isEditMode) {
    return (
      <Fragment>
        <Typography variant="p">{branch.name}</Typography>
        <Button
          size="small"
          onClick={handleDelete}
          startIcon={<RemoveBranchIcon className={`${styles.buttonIcon} ${styles.negativeIcon}`} />}
          className={styles.branchDeleteButton}
          variant="ghost-secondary"
          isLoading={isUpdatingBranches}
          disabled={isUpdatingBranches || collection.branches?.length === 1}
        />
      </Fragment>
    )
  }

  return (
    <Checkbox
      label={branch.name}
      checked={isSelected}
      name={branch.name}
      onChange={(e) => onToggle(branch.id!, (e.target as HTMLInputElement).checked)}
    />
  )
}

export default BranchItem
